import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  Check,
  ChevronDown,
  KeyRound,
  Settings,
  Users,
  Link as LinkIcon,
  ShieldCheck,
  AlertCircle,
  Plug,
} from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import OrgGeneralSection from '../../components/organization/OrgGeneralSection';
import OrgIntegrationsSection from '../../components/organization/OrgIntegrationsSection';
import OrgSlaSection from '../../components/organization/OrgSlaSection';
import OrgApiKeysSection from '../../components/organization/OrgApiKeysSection';
import OrgConnectedAppsSection from '../../components/organization/OrgConnectedAppsSection';
import MembersList from '../../components/organization/MembersList';
import { Spinner } from '../../design-system';
import PopoverPortal from '../../components/common/PopoverPortal';
import { useFeatures } from '../../hooks/useFeatures';

type SectionId = 'general' | 'members' | 'integrations' | 'sla' | 'api-keys' | 'connected-apps';

const SECTIONS: Array<{ id: SectionId; label: string; icon: React.ElementType }> = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'integrations', label: 'Integrations', icon: LinkIcon },
  { id: 'sla', label: 'SLA', icon: ShieldCheck },
  { id: 'api-keys', label: 'API Keys', icon: KeyRound },
  { id: 'connected-apps', label: 'Connected apps', icon: Plug },
];

// Sections that only exist when a deployment-level feature is enabled.
const SECTION_FEATURE_KEYS: Partial<Record<SectionId, string>> = {
  'connected-apps': 'mcp_oauth',
};

const isValidSection = (value: string | undefined): value is SectionId =>
  value === 'general' ||
  value === 'members' ||
  value === 'integrations' ||
  value === 'sla' ||
  value === 'api-keys' ||
  value === 'connected-apps';

const OrganizationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, section } = useParams<{ orgId: string; section?: string }>();
  const { organizations, isLoading } = useOrganization();
  const { features } = useFeatures();

  const sections = useMemo(
    () =>
      SECTIONS.filter((s) => {
        const featureKey = SECTION_FEATURE_KEYS[s.id];
        return !featureKey || features.includes(featureKey);
      }),
    [features],
  );

  const currentSection: SectionId =
    isValidSection(section) && sections.some((s) => s.id === section) ? section : 'general';

  const organization = useMemo(
    () => (orgId ? organizations.find((org) => org.id === orgId) ?? null : null),
    [organizations, orgId],
  );

  const handleSectionChange = (next: SectionId) => {
    if (!orgId) return;
    navigate(`/organizations/${orgId}/settings/${next}`, { replace: true });
  };

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const currentSectionMeta = sections.find((s) => s.id === currentSection) ?? sections[0];

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-center py-16">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-ui-text-secondary">
            Organization not found or you don't have access.
          </p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (currentSection) {
      case 'general':
        return <OrgGeneralSection organization={organization} />;
      case 'members':
        return (
          <MembersList
            organizationId={organization.id}
            organizationName={organization.name}
          />
        );
      case 'integrations':
        return <OrgIntegrationsSection organizationId={organization.id} />;
      case 'sla':
        return <OrgSlaSection organizationId={organization.id} />;
      case 'api-keys':
        return <OrgApiKeysSection organizationId={organization.id} />;
      case 'connected-apps':
        return <OrgConnectedAppsSection organizationId={organization.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-brand-primary/10 dark:bg-brand-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-ui-text-primary truncate">
            {organization.name}
          </h1>
          <p className="text-sm text-ui-text-tertiary mt-1">
            Organization Settings
          </p>
        </div>
      </div>

      {/* Mobile: dropdown picker */}
      <div className="md:hidden mb-4 relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => setIsPickerOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isPickerOpen}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm font-medium text-ui-text-primary"
        >
          <span className="flex items-center gap-2 min-w-0">
            <currentSectionMeta.icon size={16} className="text-brand-primary dark:text-brand-primaryDarkMode flex-shrink-0" />
            <span className="truncate">{currentSectionMeta.label}</span>
          </span>
          <ChevronDown
            size={16}
            className={`text-ui-text-tertiary transition-transform duration-150 flex-shrink-0 ${
              isPickerOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        <PopoverPortal
          anchorRef={pickerRef}
          open={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          matchAnchorWidth
          className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg py-1 overflow-hidden"
        >
          <ul role="listbox">
            {sections.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === currentSection;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      handleSectionChange(s.id);
                      setIsPickerOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                      isActive
                        ? 'bg-brand-primary/10 dark:bg-brand-primaryDarkMode/10 text-ui-text-primary font-medium'
                        : 'text-ui-text-secondary hover:bg-ui-background-tertiary dark:hover:bg-ui-background-tertiary'
                    }`}
                  >
                    <Icon
                      size={16}
                      className={isActive ? 'text-brand-primary dark:text-brand-primaryDarkMode' : 'text-ui-text-tertiary'}
                    />
                    <span className="flex-1">{s.label}</span>
                    {isActive && (
                      <Check size={16} className="text-brand-primary dark:text-brand-primaryDarkMode" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverPortal>
      </div>

      {/* Desktop: sidebar + content two-column layout */}
      <div className="md:flex md:gap-6">
        <aside className="hidden md:block w-48 flex-shrink-0">
          <nav className="space-y-1 sticky top-4">
            {sections.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === currentSection;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleSectionChange(s.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-brand-primary text-black'
                      : 'text-ui-text-secondary hover:bg-ui-background-tertiary dark:hover:bg-ui-background-tertiary'
                  }`}
                >
                  <Icon size={16} />
                  {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettingsPage;
